import { inject, onScopeDispose, provide, watch, type InjectionKey, type Ref } from 'vue';

/**
 * Floating content a table owns: every open popover, dropdown, modal, floating
 * panel and drawer of the kit claims itself for the table it was opened from —
 * the table's own dialogs and row menu (rendered next to its grid), a cell's
 * date picker or autocomplete (rendered in place), a panel teleported to
 * `<body>`. Focus inside a claimed element still counts as working in the
 * table, so its lazy editors stay mounted.
 *
 * Nothing else outside the table counts: an unrelated menu or popover that
 * happens to be open elsewhere on the page ends editing like any other focus
 * target (I-2172).
 */
export type NxTableFocusScope = {
  /** Claims `el` for this table; the returned function releases it again. */
  claim(el: HTMLElement): () => void;
  /** Whether `node` lies inside an element claimed for this table. */
  owns(node: Node): boolean;
};

export const nxTableFocusScopeKey: InjectionKey<NxTableFocusScope> = Symbol('nxTableFocusScope');

/**
 * The scope of one table, provided to everything rendered below it. A claim is
 * passed on to an enclosing table as well: a table inside a teleported drawer
 * of another table keeps both of them in edit mode.
 */
export function createTableFocusScope(): NxTableFocusScope {
  const parent = inject(nxTableFocusScopeKey, null);
  const claimed = new Set<HTMLElement>();
  const scope: NxTableFocusScope = {
    claim(el) {
      claimed.add(el);
      const releaseParent = parent?.claim(el);
      return () => {
        claimed.delete(el);
        releaseParent?.();
      };
    },
    owns(node) {
      for (const el of claimed) {
        if (el.contains(node)) return true;
      }
      return false;
    },
  };
  provide(nxTableFocusScopeKey, scope);
  return scope;
}

/**
 * For the kit's floating components: claims `el` for the enclosing table while
 * `active()` holds. Outside a table this does nothing.
 */
export function useTableFocusClaim(
  el: Readonly<Ref<HTMLElement | null | undefined>>,
  active: () => boolean
): void {
  const scope = inject(nxTableFocusScopeKey, null);
  if (!scope) return;
  let release: (() => void) | null = null;
  watch(
    [el, active],
    ([node, on]) => {
      release?.();
      release = node && on ? scope.claim(node) : null;
    },
    { immediate: true, flush: 'post' }
  );
  onScopeDispose(() => release?.());
}
